import { directSupabase } from '../lib/supabaseClient';
import { renderWindowGeneratorBaySchematicToPngBlob, renderWindowGeneratorPayloadToPngBlob } from '../utils/windowGeneratorExport';
import type { WindowGeneratorPayload } from './windowGeneratorTemplates.service';
import { generateUuid } from '../utils/uuid-helper';

const BUCKET = 'visual-designer-previews';

const BUCKET_SETUP_MSG = `Create the bucket in Supabase Dashboard: Storage → New bucket → Name: "${BUCKET}" → Public: On → Create. Then add a policy so authenticated users can upload (INSERT).`;

function isBayLikePayload(payload: WindowGeneratorPayload): boolean {
  const p: any = payload || {};
  const preset = String(p?.root?.preset || '').trim().toLowerCase();
  return preset === 'bay';
}

function extFromMimeType(mime: string): 'png' | 'jpg' {
  const mt = String(mime || '').toLowerCase();
  if (mt.includes('png')) return 'png';
  // treat everything else as jpeg-compatible
  return 'jpg';
}

async function loadImageForCompression(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for compression'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function compressReferenceImage(file: File): Promise<{ blob: Blob; mime: string; ext: 'jpg' }> {
  if (typeof document === 'undefined') {
    return { blob: file, mime: String(file.type || 'image/jpeg'), ext: 'jpg' };
  }

  const img = await loadImageForCompression(file);
  // Keep the visual dimensions largely intact; reduce file weight by JPEG re-encoding.
  // The cap only protects against very large camera originals.
  const maxSidePx = 4096;
  const sourceW = Math.max(1, img.naturalWidth || img.width || 1);
  const sourceH = Math.max(1, img.naturalHeight || img.height || 1);
  const scale = Math.min(1, maxSidePx / Math.max(sourceW, sourceH));
  const targetW = Math.max(1, Math.round(sourceW * scale));
  const targetH = Math.max(1, Math.round(sourceH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return { blob: file, mime: String(file.type || 'image/jpeg'), ext: 'jpg' };
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(img, 0, 0, targetW, targetH);

  const compressed = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.72);
  });
  if (!compressed) return { blob: file, mime: String(file.type || 'image/jpeg'), ext: 'jpg' };
  return { blob: compressed, mime: 'image/jpeg', ext: 'jpg' };
}

export async function uploadVisualDesignerReferenceImageVariantAndGetUrl(args: {
  file: File;
  baseId?: string;
  variant: 'external' | 'internal' | 'presentation-external' | 'presentation-internal' | 'design-external' | 'design-internal';
}): Promise<{ baseId: string; publicUrl: string }> {
  const { file, variant } = args;

  const { data: auth } = await directSupabase.auth.getUser();
  if (!auth.user) throw new Error('Not authenticated');

  const mime = String(file?.type || '').toLowerCase();
  if (mime !== 'image/png' && mime !== 'image/jpeg') {
    throw new Error('Unsupported file type. Please upload a PNG or JPEG image.');
  }

  const baseId = args.baseId || generateUuid();
  const upload = await compressReferenceImage(file);
  const ext = upload.ext || extFromMimeType(upload.mime);
  const path = `${baseId}-reference-${variant}.${ext}`;

  const { error } = await directSupabase.storage
    .from(BUCKET)
    .upload(path, upload.blob, { contentType: upload.mime, upsert: false });

  if (error) {
    const msg = error.message?.toLowerCase() ?? '';
    if (msg.includes('bucket not found') || msg.includes('not found')) {
      throw new Error(`Storage bucket "${BUCKET}" not found. ${BUCKET_SETUP_MSG}`);
    }
    throw new Error(error.message || 'Failed to upload reference image');
  }

  const { data: urlData } = directSupabase.storage.from(BUCKET).getPublicUrl(path);
  return { baseId, publicUrl: urlData.publicUrl };
}

/**
 * Uploads an arbitrary user-provided reference image (JPEG/PNG) to Supabase Storage and returns the public URL.
 * Used by Visual Designer v2 Lite to attach an ad-hoc style reference image to a quote line.
 */
export async function uploadVisualDesignerReferenceImageAndGetUrl(
  file: File
): Promise<{ id: string; publicUrl: string }> {
  // Back-compat wrapper: treat as "external" reference only.
  const { baseId, publicUrl } = await uploadVisualDesignerReferenceImageVariantAndGetUrl({
    file,
    variant: 'external',
  });
  return { id: baseId, publicUrl };
}

/**
 * Uploads a Window Generator payload as a PNG to Supabase Storage and returns the public URL.
 * Used when saving a VD line with a WG style so the image can be sent in supplier emails.
 * Requires the bucket "visual-designer-previews" to exist (create it in Dashboard if you see Bucket not found).
 */
export async function uploadVisualDesignerPreviewAndGetUrl(
  payload: WindowGeneratorPayload
): Promise<string> {
  const blob = await renderWindowGeneratorPayloadToPngBlob(payload, { view: 'external' });
  const path = `${generateUuid()}-external.png`;
  const { data: auth } = await directSupabase.auth.getUser();
  if (!auth.user) throw new Error('Not authenticated');

  const { error } = await directSupabase.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: 'image/png', upsert: false });

  if (error) {
    const msg = error.message?.toLowerCase() ?? '';
    if (msg.includes('bucket not found') || msg.includes('not found')) {
      throw new Error(`Storage bucket "${BUCKET}" not found. ${BUCKET_SETUP_MSG}`);
    }
    throw new Error(error.message || 'Failed to upload preview image');
  }

  const { data: urlData } = directSupabase.storage.from(BUCKET).getPublicUrl(path);
  return urlData.publicUrl;
}

/**
 * Uploads BOTH external + internal views for a Window Generator payload.
 * v2 uses this so downstream templates can render both perspectives.
 */
export async function uploadVisualDesignerPreviewsAndGetUrls(
  payload: WindowGeneratorPayload
): Promise<{
  designExternalUrl: string;
  designInternalUrl: string;
  presentationExternalUrl: string;
  presentationInternalUrl: string;
  baySchematicUrl: string;
}> {
  const isBayPayload = isBayLikePayload(payload);
  const [designExternalBlob, designInternalBlob, presentationExternalBlob, presentationInternalBlob, baySchematicBlob] = await Promise.all([
    renderWindowGeneratorPayloadToPngBlob(payload, { view: 'external', mode: 'design' }),
    renderWindowGeneratorPayloadToPngBlob(payload, { view: 'internal', mode: 'design' }),
    renderWindowGeneratorPayloadToPngBlob(payload, { view: 'external', mode: 'presentation' }),
    renderWindowGeneratorPayloadToPngBlob(payload, { view: 'internal', mode: 'presentation' }),
    isBayPayload ? renderWindowGeneratorBaySchematicToPngBlob(payload) : Promise.resolve(null as Blob | null),
  ]);

  const designExternalPath = `${generateUuid()}-design-external.png`;
  const designInternalPath = `${generateUuid()}-design-internal.png`;
  const presentationExternalPath = `${generateUuid()}-presentation-external.png`;
  const presentationInternalPath = `${generateUuid()}-presentation-internal.png`;
  const baySchematicPath = isBayPayload ? `${generateUuid()}-bay-schematic.png` : '';

  const { data: auth } = await directSupabase.auth.getUser();
  if (!auth.user) throw new Error('Not authenticated');

  const up = async (path: string, blob: Blob) => {
    const { error } = await directSupabase.storage
      .from(BUCKET)
      .upload(path, blob, { contentType: 'image/png', upsert: false });
    if (error) {
      const msg = error.message?.toLowerCase() ?? '';
      if (msg.includes('bucket not found') || msg.includes('not found')) {
        throw new Error(`Storage bucket "${BUCKET}" not found. ${BUCKET_SETUP_MSG}`);
      }
      throw new Error(error.message || 'Failed to upload preview image');
    }
    const { data: urlData } = directSupabase.storage.from(BUCKET).getPublicUrl(path);
    return urlData.publicUrl;
  };

  const [designExternalUrl, designInternalUrl, presentationExternalUrl, presentationInternalUrl] = await Promise.all([
    up(designExternalPath, designExternalBlob),
    up(designInternalPath, designInternalBlob),
    up(presentationExternalPath, presentationExternalBlob),
    up(presentationInternalPath, presentationInternalBlob),
  ]);
  const baySchematicUrl =
    isBayPayload && baySchematicBlob && baySchematicPath
      ? await up(baySchematicPath, baySchematicBlob)
      : '';

  return { designExternalUrl, designInternalUrl, presentationExternalUrl, presentationInternalUrl, baySchematicUrl };
}
